/**
 * 站点设置组合式函数
 * 提供全局的站点设置管理功能
 */

import { ref, watch } from 'vue'
import { updateFavicon } from '@/utils/favicon'
import { settings } from '@/api/index'
import storage, { STORAGE_KEYS } from '@/utils/storage'

// 全局站点设置状态
const siteSettings = ref({
  siteName: 'DDY授权系统',
  siteDesc: '专业的身份认证与授权管理系统',
  version: 'v1.0.0',
  favicon: '/upload/favicon/favicon_1754488478.ico',
  loginTitle: '哆哆鱼授权系统',
  loginSubtitle: '专业的身份认证与授权管理系统',
  copyright: 'Copyright © 2025 哆哆鱼SaaS认证系统 All rights reserved.',
  icp: '陕ICP备20002159号',
  techSupport: '哆哆鱼技术团队',
  servicePhone: '029-11934356'
})

// 加载状态
const loading = ref(false)

/**
 * 使用站点设置的组合式函数
 * @returns {Object} 站点设置相关的响应式数据和方法
 */
export function useSiteSettings() {
  /**
   * 获取站点设置
   * @returns {Promise<void>}
   */
  const fetchSiteSettings = async (useCache = true) => {
    try {
      loading.value = true
      const response = await settings.getSiteSettings()

      if ((response.code === 0 || response.code === 200) && response.data) {
        // 更新全局设置
        siteSettings.value = {
          ...siteSettings.value,
          ...response.data
        }

        // 保存到本地存储
        storage.set(STORAGE_KEYS.SITE_SETTINGS, siteSettings.value)

        // 更新页面标题和favicon
        updatePageMeta()
      } else {
        throw new Error('API返回错误，code: ' + response.code + ', message: ' + (response.message || '未知错误'))
      }
    } catch (error) {
      console.warn('获取站点设置失败:', error.message)
      // 只有在允许使用缓存时才从本地存储获取
      if (useCache) {
        const cachedSettings = storage.get(STORAGE_KEYS.SITE_SETTINGS)
        if (cachedSettings) {
          siteSettings.value = {
            ...siteSettings.value,
            ...cachedSettings
          }
          updatePageMeta()
        }
      } else {
        // 不使用缓存时，抛出错误
        throw error
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新站点设置
   * @param {Object} newSettings - 新的设置数据
   * @returns {Promise<boolean>} 是否更新成功
   */
  const updateSiteSettings = async (newSettings) => {
    try {
      loading.value = true
      const response = await settings.updateSiteSettings(newSettings)

      if (response.code === 0 || response.code === 200) {
        // 更新全局设置
        siteSettings.value = {
          ...siteSettings.value,
          ...newSettings
        }

        // 保存到本地存储
        storage.set(STORAGE_KEYS.SITE_SETTINGS, siteSettings.value)

        // 更新页面标题和favicon
        updatePageMeta()

        return true
      }
      return false
    } catch (error) {
      console.error('更新站点设置失败:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新页面元信息（标题和favicon）
   */
  const updatePageMeta = () => {
    // 更新页面标题
    if (siteSettings.value.siteName) {
      document.title = siteSettings.value.siteName
    }

    // 更新favicon
    if (siteSettings.value.favicon) {
      updateFavicon(siteSettings.value.favicon)
    }
  }

  /**
   * 获取特定的设置值
   * @param {string} key - 设置键名
   * @param {any} defaultValue - 默认值
   * @returns {any} 设置值
   */
  const getSetting = (key, defaultValue = '') => {
    return siteSettings.value[key] || defaultValue
  }

  /**
   * 设置特定的设置值（仅本地，不保存到服务器）
   * @param {string} key - 设置键名
   * @param {any} value - 设置值
   */
  const setSetting = (key, value) => {
    siteSettings.value[key] = value

    // 如果是影响页面显示的设置，立即更新
    if (key === 'siteName' || key === 'favicon') {
      updatePageMeta()
    }
  }

  /**
   * 重置为默认设置
   */
  const resetToDefaults = () => {
    siteSettings.value = {
      siteName: 'DDY授权系统',
      siteDesc: '专业的身份认证与授权管理系统',
      version: 'v1.0.0',
      favicon: '/upload/favicon/favicon_1754488478.ico',
      loginTitle: '哆哆鱼授权系统',
      loginSubtitle: '专业的身份认证与授权管理系统',
      copyright: 'Copyright © 2025 哆哆鱼SaaS认证系统 All rights reserved.',
      icp: '陕ICP备20002159号',
      techSupport: '哆哆鱼技术团队',
      servicePhone: '029-11934356'
    }
    updatePageMeta()
  }

  // 监听favicon变化，自动更新页面图标
  watch(
    () => siteSettings.value.favicon,
    (newFavicon) => {
      if (newFavicon) {
        updateFavicon(newFavicon)
      }
    },
    { immediate: false }
  )

  // 监听站点名称变化，自动更新页面标题
  watch(
    () => siteSettings.value.siteName,
    (newTitle) => {
      if (newTitle) {
        document.title = newTitle
      }
    },
    { immediate: false }
  )

  return {
    // 响应式数据
    siteSettings,
    loading,

    // 方法
    fetchSiteSettings,
    updateSiteSettings,
    updatePageMeta,
    getSetting,
    setSetting,
    resetToDefaults
  }
}

// 导出全局设置状态，供其他地方直接访问
export { siteSettings }